import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Upload, CheckSquare, Square, ChevronRight, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { db } from "@/lib/firebase";
import { collection, addDoc } from "firebase/firestore";
import { incrementCounter } from "@/lib/stats";
import { uploadToCloudinary } from "@/lib/cloudinary";

export type FieldType =
  | "text" | "email" | "tel" | "date" | "url" | "number"
  | "textarea" | "select" | "radio" | "checkboxes" | "checkbox"
  | "file" | "signature";

export interface FormField {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: string[];
  accept?: string;
  maxSizeMB?: number;
}

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  infoContent?: string;
  fields: FormField[];
}

export interface FormSchema {
  sections: FormSection[];
  submitButtonText?: string;
  successMessage?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/60 transition";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-white/60 mb-1.5";

function InfoBlock({ content }: { content: string }) {
  const lines = content.split("\n");
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 mb-6 text-sm text-amber-200 leading-relaxed whitespace-pre-line">
      {lines.map((l, i) => (
        <span key={i}>
          {l.startsWith("**") && l.endsWith("**") ? (
            <strong className="text-amber-400 font-bold">{l.replace(/\*\*/g, "")}</strong>
          ) : l.startsWith("- ") ? (
            <span className="flex gap-2"><span className="text-primary">•</span>{l.slice(2)}</span>
          ) : l}
          {"\n"}
        </span>
      ))}
    </div>
  );
}

// ── Individual Field ────────────────────────────────────────────────────────

function FormFieldRenderer({
  field,
  value,
  onChange,
  onFileChange,
  error,
}: {
  field: FormField;
  value: any;
  onChange: (id: string, val: any) => void;
  onFileChange: (id: string, file: File | null) => void;
  error?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  const base = (
    <>
      <label className={labelCls}>
        {field.label}
        {field.required && <span className="text-primary ml-1">*</span>}
      </label>
      {field.description && (
        <p className="text-xs text-white/40 mb-2 whitespace-pre-line leading-relaxed">{field.description}</p>
      )}
    </>
  );

  const errMsg = error && (
    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
      <AlertCircle size={11} /> {error}
    </p>
  );

  if (field.type === "textarea") {
    return (
      <div>
        {base}
        <textarea
          value={value || ""}
          onChange={e => onChange(field.id, e.target.value)}
          placeholder={field.placeholder || ""}
          rows={4}
          className={inputCls + " resize-none " + (error ? "border-red-500/60" : "")}
        />
        {errMsg}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        {base}
        <select
          value={value || ""}
          onChange={e => onChange(field.id, e.target.value)}
          className={inputCls + " " + (error ? "border-red-500/60" : "")}
        >
          <option value="">— Select —</option>
          {(field.options || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        {errMsg}
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div>
        {base}
        <div className="flex flex-wrap gap-3">
          {(field.options || []).map(opt => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer group">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition
                ${value === opt ? "border-primary bg-primary" : "border-white/30 group-hover:border-primary/60"}`}>
                {value === opt && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <input type="radio" value={opt} checked={value === opt}
                onChange={() => onChange(field.id, opt)} className="sr-only" />
              <span className="text-sm text-white/80">{opt}</span>
            </label>
          ))}
        </div>
        {errMsg}
      </div>
    );
  }

  if (field.type === "checkboxes") {
    const selected: string[] = Array.isArray(value) ? value : [];
    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter(s => s !== opt)
        : [...selected, opt];
      onChange(field.id, next);
    };
    return (
      <div>
        {base}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(field.options || []).map(opt => (
            <label key={opt} className="flex items-center gap-2.5 cursor-pointer group">
              <div onClick={() => toggle(opt)}
                className={`w-4 h-4 rounded border-2 flex items-center justify-center transition shrink-0
                ${selected.includes(opt) ? "border-primary bg-primary" : "border-white/30 group-hover:border-primary/60"}`}>
                {selected.includes(opt) && <CheckSquare size={10} className="text-white fill-white" />}
              </div>
              <span className="text-sm text-white/80">{opt}</span>
            </label>
          ))}
        </div>
        {errMsg}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <div onClick={() => onChange(field.id, !value)}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition shrink-0 mt-0.5
            ${value ? "border-primary bg-primary" : "border-white/30 group-hover:border-primary/60"}`}>
            {value && <CheckSquare size={12} className="text-white fill-white" />}
          </div>
          <span className="text-sm text-white/80 leading-relaxed">
            {field.label}{field.required && <span className="text-primary ml-1">*</span>}
          </span>
        </label>
        {errMsg}
      </div>
    );
  }

  if (field.type === "file") {
    const fileVal = value as File | null;
    return (
      <div>
        {base}
        <input ref={fileRef} type="file" accept={field.accept} className="hidden"
          onChange={e => onFileChange(field.id, e.target.files?.[0] ?? null)} />
        <button type="button" onClick={() => fileRef.current?.click()}
          className={`w-full border-2 border-dashed rounded-xl py-5 flex flex-col items-center gap-2 transition
          ${error ? "border-red-500/50" : "border-white/10 hover:border-primary/50"}`}>
          <Upload size={20} className="text-white/40" />
          {fileVal
            ? <span className="text-sm text-primary font-medium">{fileVal.name}</span>
            : <span className="text-sm text-white/40">Click to upload</span>}
          {field.description && <span className="text-xs text-white/30">{field.description}</span>}
        </button>
        {errMsg}
      </div>
    );
  }

  if (field.type === "signature") {
    return (
      <div>
        <label className={labelCls}>
          {field.label}{field.required && <span className="text-primary ml-1">*</span>}
        </label>
        {field.description && <p className="text-xs text-white/40 mb-2">{field.description}</p>}
        <input
          type="text"
          value={value || ""}
          onChange={e => onChange(field.id, e.target.value)}
          placeholder="Type your full name"
          className={inputCls + " font-signature text-xl italic tracking-wide " + (error ? "border-red-500/60" : "")}
        />
        {errMsg}
      </div>
    );
  }

  // default: text / email / tel / date / url / number
  return (
    <div>
      {base}
      <input
        type={field.type}
        value={value || ""}
        onChange={e => onChange(field.id, e.target.value)}
        placeholder={field.placeholder || ""}
        className={inputCls + " " + (error ? "border-red-500/60" : "")}
      />
      {errMsg}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function DynamicApplicationForm({
  schema,
  formType,
}: {
  schema: FormSchema;
  formType: "athlete" | "coach";
}) {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<Record<string, File>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const allFields = schema.sections.flatMap(s => s.fields);

  const validate = () => {
    const errs: Record<string, string> = {};
    for (const field of allFields) {
      if (!field.required) continue;
      const val = field.type === "file" ? files[field.id] : values[field.id];
      const empty =
        val === undefined || val === null || val === "" ||
        (Array.isArray(val) && val.length === 0) ||
        (field.type === "checkbox" && !val);
      if (empty) errs[field.id] = "This field is required.";
    }
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      // scroll to first error
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast({ title: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const uploadedUrls: Record<string, string> = {};
      for (const [k, f] of Object.entries(files)) {
        const url = await uploadToCloudinary(f);
        uploadedUrls[k] = url;
      }

      const submissionData = { ...values };
      for (const [k, url] of Object.entries(uploadedUrls)) {
        submissionData[k] = url;
      }

      const appRef = await addDoc(collection(db, "applications"), {
        ...submissionData,
        formType,
        status: "pending",
        notes: "",
        createdAt: new Date().toISOString(),
      });

      // increment lightweight counter for faster dashboard totals (best-effort)
      try {
        await incrementCounter("applications");
      } catch (e) {
        // non-fatal
      }

      setSubmitted(true);
    } catch (err: any) {
      toast({ title: "Submission failed", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-20 px-6 bg-white/5 rounded-3xl border border-white/10 max-w-2xl mx-auto"
      >
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
          <CheckSquare className="text-primary" size={32} />
        </div>
        <h2 className="text-2xl font-display font-bold text-white mb-4">Application Submitted!</h2>
        <p className="text-white/60 leading-relaxed">
          {schema.successMessage || "Thank you for your application. We will review it and be in touch soon."}
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="space-y-12">
        {schema.sections.map((section, si) => (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: si * 0.05 }}
            className="bg-white/[0.03] border border-white/8 rounded-3xl p-6 sm:p-8"
          >
            <h2 className="text-lg font-display font-bold text-primary mb-1 tracking-wide">
              {section.title}
            </h2>
            {section.description && (
              <p className="text-sm text-white/50 mb-6 leading-relaxed whitespace-pre-line">
                {section.description}
              </p>
            )}
            {section.infoContent && <InfoBlock content={section.infoContent} />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {section.fields.map(field => {
                const wide = ["textarea", "checkboxes", "radio", "file", "checkbox", "signature"].includes(field.type)
                  || field.id.includes("highlights") || field.id.includes("previous") || field.id.includes("philosophy")
                  || field.id.includes("address") || field.id.includes("reason") || field.id.includes("why");
                return (
                  <div key={field.id} id={`field-${field.id}`}
                    className={wide ? "sm:col-span-2" : ""}>
                    <FormFieldRenderer
                      field={field}
                      value={values[field.id]}
                      onChange={(id, val) => {
                        setValues(v => ({ ...v, [id]: val }));
                        if (errors[id]) setErrors(e => { const n = { ...e }; delete n[id]; return n; });
                      }}
                      onFileChange={(id, file) => {
                        if (file) {
                          setFiles(f => ({ ...f, [id]: file }));
                          setValues(v => ({ ...v, [id]: file.name }));
                        }
                        if (errors[id]) setErrors(e => { const n = { ...e }; delete n[id]; return n; });
                      }}
                      error={errors[field.id]}
                    />
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-3 bg-primary text-[#0a0f1a] font-display font-bold text-base px-12 py-4 rounded-2xl hover:bg-primary/90 disabled:opacity-60 transition-all hover:shadow-xl hover:shadow-primary/30 hover:scale-105 active:scale-95"
        >
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <ChevronRight size={18} />}
          {submitting ? "Submitting…" : (schema.submitButtonText || "Submit Application")}
        </button>
        <p className="text-xs text-white/30 mt-4">
          Fields marked with <span className="text-primary">*</span> are required.
        </p>
      </div>
    </form>
  );
}
